import {
  arrayUnion,
  arrayRemove,
  collection,
  CollectionReference,
  doc,
  FieldValue,
  getDoc,
  getDocs,
  setDoc,
  query,
  updateDoc,
  deleteDoc,
  where,
  orderBy
} from 'firebase/firestore'
import { Timestamp } from '@/modules/datetime'
import { firestore } from '@/modules/firebase'

const teamCollection = collection(
  firestore,
  'teams'
) as CollectionReference<Team>
const getTeamMemberCollection = (teamId: string) => {
  return collection(
    teamCollection,
    teamId,
    'memberAttributes'
  ) as CollectionReference<UserMetaData>
}
const getTeamInvitationsCollection = (teamId: string) => {
  return collection(
    teamCollection,
    teamId,
    'invitations'
  ) as CollectionReference<TeamInvite>
}

export async function getTeam(teamId: string) {
  const snapshot = await getDoc(doc(teamCollection, teamId))
  const teamData = snapshot.data()
  if (teamData) {
    return teamData
  } else throw new Error('No matching document found')
}

export async function updateTeamSettings(
  teamId: string,
  updates: Partial<Pick<Team, 'allowMemberEvents' | 'name' | 'description'>>
) {
  const teamDoc = doc(teamCollection, teamId)
  return updateDoc(teamDoc, updates)
}

export async function addTeamMember(
  teamId: string,
  userId: string,
  asAdmin = false
) {
  const teamDoc = doc(teamCollection, teamId)
  const updates: { memberIds: FieldValue; adminIds?: FieldValue } = {
    memberIds: arrayUnion(userId)
  }
  if (asAdmin) {
    updates.adminIds = arrayUnion(userId)
  }
  return updateDoc(teamDoc, updates)
}

export async function getTeamsOfUser(userId: string) {
  const teamsQuery = query(
    teamCollection,
    where('memberIds', 'array-contains', userId)
  )
  const querySnapshot = await getDocs(teamsQuery)
  return querySnapshot.docs.map((doc) => doc.data())
}

export async function getMemberPropertiesMap(teamId: string) {
  const teamMemberCollection = getTeamMemberCollection(teamId)
  const docsSnapshot = await getDocs(teamMemberCollection)
  return docsSnapshot.docs.reduce((map, propertiesDoc) => {
    return map.set(propertiesDoc.id, propertiesDoc.data())
  }, new Map<string, UserMetaData>())
}

export async function getTeamMemberProperties(
  teamId: string,
  userId: string
): Promise<Partial<UserMetaData>> {
  const teamMemberCollection = getTeamMemberCollection(teamId)
  const teamMemberDoc = doc(teamMemberCollection, userId)
  const snapshot = await getDoc(teamMemberDoc)
  if (snapshot.exists()) {
    return snapshot.data()
  } else return {}
}

export async function setMemberProperties(
  teamId: string,
  userId: string,
  metadata: Partial<UserMetaData>
) {
  const teamMemberCollection = getTeamMemberCollection(teamId)
  const teamMemberDoc = doc(teamMemberCollection, userId)
  return setDoc(teamMemberDoc, metadata, { merge: true })
}

export async function getLocationItems(teamId: string) {
  const teamData = (await getDoc(doc(teamCollection, teamId))).data()
  return teamData?.savedLocations ?? []
}

export async function addLocationItem(teamId: string, itemName: string) {
  const teamDoc = doc(teamCollection, teamId)
  return updateDoc(teamDoc, {
    savedLocations: arrayUnion(itemName)
  })
}

export async function removeLocationItem(teamId: string, itemName: string) {
  const teamDoc = doc(teamCollection, teamId)
  return updateDoc(teamDoc, {
    savedLocations: arrayRemove(itemName)
  })
}

export async function removeMember(teamId: string, userId: string) {
  return removeMembers(teamId, [userId])
}

export async function removeMembers(teamId: string, userIds: string[]) {
  const teamDoc = doc(teamCollection, teamId)
  return updateDoc(teamDoc, {
    memberIds: arrayRemove(...userIds),
    coachIds: arrayRemove(...userIds),
    adminIds: arrayRemove(...userIds)
  })
}

export async function updateMemberRole(
  teamId: string,
  userId: string,
  newRole: TeamRole
) {
  return updateMemberRoles(teamId, [userId], newRole)
}

export async function updateMemberRoles(
  teamId: string,
  userIds: string[],
  newRole: TeamRole
) {
  const teamDoc = doc(teamCollection, teamId)

  switch (newRole) {
    case 'member':
      return updateDoc(teamDoc, {
        coachIds: arrayRemove(...userIds),
        adminIds: arrayRemove(...userIds)
      })
    case 'coach':
      return updateDoc(teamDoc, {
        coachIds: arrayUnion(...userIds),
        adminIds: arrayRemove(...userIds)
      })
    case 'admin':
      return updateDoc(teamDoc, {
        coachIds: arrayRemove(...userIds),
        adminIds: arrayUnion(...userIds)
      })
  }
}

export async function getInvitations(teamId: string) {
  const teamInvitationsCollection = getTeamInvitationsCollection(teamId)
  const docsSnapshot = await getDocs(
    query(teamInvitationsCollection, orderBy('timestamp', 'desc'))
  )

  return docsSnapshot.docs.map((doc) => doc.data())
}

export async function createInvitation(teamId: string) {
  const teamInvitationsCollection = getTeamInvitationsCollection(teamId)
  const newDocRef = doc(teamInvitationsCollection)
  await setDoc(newDocRef, {
    inviteId: newDocRef.id,
    timestamp: Timestamp.now(),
    teamId
  })
  return newDocRef.id
}

export async function verifyInvitation(teamId: string, invitationId: string) {
  const teamInvitationsCollection = getTeamInvitationsCollection(teamId)
  const invitationDoc = await getDoc(
    doc(teamInvitationsCollection, invitationId)
  )

  if (!invitationDoc.exists()) return false

  const referenceDate = new Date()
  referenceDate.setDate(referenceDate.getDate() - 3)

  return invitationDoc.data().timestamp.toDate() > referenceDate
}

export async function deleteInvitation(teamId: string, invitationId: string) {
  const teamInvitationsCollection = getTeamInvitationsCollection(teamId)
  return deleteDoc(doc(teamInvitationsCollection, invitationId))
}
